import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { toast } from "react-toastify";
import swal from "sweetalert";
import moment from "moment";
import FormControl from "../../components/common/FormControl";
import methodModel from "../../methods/methods";
import { IoArrowBack } from "react-icons/io5";

function stripHtmlTags(html) {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
}

const roundSmart = (num) => {
  try {
    return methodModel.truncateDecimal(num || 0);
  } catch {
    return methodModel.truncateDecimal(num || 0);
  }
};

const parsePrivateNote = (note) => {
  if (!note) return {};
  const getValue = (label) => {
    const match = note.match(new RegExp(`${label}:\\s*(.*)`));
    return match ? match[1].trim() : "";
  };
  return {
    memo: getValue("Memo"),
    AcceptedBy: getValue("Accepted By"),
    AcceptedDate: getValue("Accepted Date"),
    shipping_corp: getValue("Shipping Corp"),
    vendor_details: getValue("Vendor"),
    store_name: getValue("StoreName"),
    po_number: getValue("PoNumber"),
    ship_date: getValue("ShipDate"),
    tracking_num: getValue("TrackingNum"),
  };
};

const INITIAL_LINE = {
  DetailType: "SalesItemLineDetail",
  SalesItemLineDetail: {
    ItemRef: { value: "", name: "" },
    UnitPrice: 0,
    Qty: 1,
  },
  Description: "",
  Amount: 0,
  item_name: "",
};

const INITIAL_FORM = {
  CustomerRef: "",
  BillEmail: "",
  BillAddr: "",
  ShipAddr: "",
  billingAddress: {
    bill_line1: "",
    bill_line2: "",
    bill_city: "",
    bill_country: "",
    bill_postal_code: "",
  },
  shippingAddress: {
    ship_line1: "",
    ship_line2: "",
    ship_city: "",
    ship_country: "",
    ship_postal_code: "",
  },
  ShipVia: "",
  ShipDate: "",
  TrackingNum: "",
  DocNumber: "",
  TxnDate: new Date().toISOString().split("T")[0],
  ExpirationDate: "",
  AcceptedBy: "",
  AcceptedDate: "",
  store_name: "",
  po_number: "",
  vendor_details: "",
  Line: [INITIAL_LINE],
  tax_type: "automatic",
  tax_rate_id: "",
  total_tax: 0,
  discount_value: 0,
  discount_type: "fixed",
  discount_amt: 0,
  markup_type: "percentage",
  markup_value: 0,
  CustomerMemo: "",
  PrivateNote: "",
  TotalAmt: 0,
  tax_percent: "",
  // ─── Partial invoice fields ───────────────────────────────────────────────
  invoiceType: "full", // 'full' | 'partial'
  partialValue: "", // numeric amount entered by user
  partialMode: "fixed", // 'percentage' | 'fixed'
};

export default function GenerateQuote() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { id } = useParams();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showShipDateModal, setShowShipDateModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  // Partial invoice field-level error
  const [partialError, setPartialError] = useState("");

  const detailRef = useRef(null);
  const customersRef = useRef([]);
  const productsRef = useRef([]);

  // ─── Derived totals ───────────────────────────────────────────────────────
  const subtotalbeforemarkup = formData.TotalAmt;
  const markupAmt =
    formData.markup_type === "percentage"
      ? (Number(formData.markup_value) / 100) * subtotalbeforemarkup
      : Number(formData.markup_value) || 0;
  const subtotal = formData.TotalAmt + markupAmt;

  const taxAmt = Number(formData.total_tax);
  const discountAmt =
    formData.discount_type === "percent"
      ? Number(formData.discount_amt) || 0
      : Number(formData.discount_value) || 0;
  const estimateTotal = subtotal - discountAmt + taxAmt;

  // ─── Derived partial amount ───────────────────────────────────────────────
  const computedPartialAmt =
    formData.invoiceType === "partial" && Number(formData.partialValue) > 0
      ? formData.partialMode === "percentage"
        ? (Number(formData.partialValue) / 100) * estimateTotal
        : Number(formData.partialValue)
      : null;

  // ─── Partial validation helper ─────────────────────────────────────────────
  const validatePartial = (data = formData) => {
    if (data.invoiceType !== "partial") {
      setPartialError("");
      return true;
    }

    const val = Number(data.partialValue);
    if (!data.partialValue && data.partialValue !== 0) {
      setPartialError("Partial amount is required.");
      return false;
    }
    if (computedPartialAmt && Number(computedPartialAmt) < 0.5) {
      setPartialError("Partial amount must be greater than 0.5");
      return false;
    }
    if (isNaN(val) || val <= 0) {
      setPartialError("Partial amount must be greater than 0.");
      return false;
    }
    if (data.partialMode === "percentage" && val > 100) {
      setPartialError("Percentage cannot exceed 100.");
      return false;
    }
    if (data.partialMode === "fixed" && val > estimateTotal) {
      setPartialError(
        `Fixed amount cannot exceed the estimate total ($${methodModel.truncateDecimal(estimateTotal || 0)}).`,
      );
      return false;
    }
    setPartialError("");
    return true;
  };

  // ─── Autofill ─────────────────────────────────────────────────────────────
  const tryAutofill = (custList, prodList, detail) => {
    console.log("Autofilling with detail:", detail);
    if (!detail) return;
    const matchedCustomer = detail.customerName
      ? custList.find(
        (c) =>
          c.DisplayName?.toLowerCase() === detail.customerName.toLowerCase(),
      )
      : null;

    const buildAddr = (addr) =>
      [
        addr?.Line1,
        addr?.City || addr?.Line2,
        addr?.Country || addr?.Line3,
        addr?.PostalCode,
      ]
        .filter(Boolean)
        .join(", ");

    const bill = detail?.raw?.BillAddr || {};
    const ship = detail?.raw?.ShipAddr || {};

    const mappedLines = (detail?.originalLineItems || detail.Line || [])
      .filter((item) => item.qboItemId)
      .map((item) => {
        const matchedProd =
          prodList.find(
            (p) =>
              p.Id ===
              (item?.qboItemId || item?.SalesItemLineDetail?.ItemRef?.value),
          ) ||
          prodList.find(
            (p) =>
              p.Description?.toLowerCase() ===
              (item.description?.toLowerCase() ||
                item.SalesItemLineDetail?.ItemRef?.name?.toLowerCase()),
          );

        const unitPrice =
          item.SalesItemLineDetail?.UnitPrice ||
          item.Amount ||
          matchedProd?.UnitPrice ||
          item?.unitPrice ||
          0;
        const qty = item.SalesItemLineDetail?.Qty || item?.qty || 1;

        return {
          DetailType: "SalesItemLineDetail",
          SalesItemLineDetail: {
            ItemRef: matchedProd
              ? { value: matchedProd.Id, name: matchedProd.Name }
              : { value: "", name: "" },
            UnitPrice: unitPrice,
            Qty: qty,
          },
          Description: item.Description || matchedProd?.Description || "",
          Amount: qty * unitPrice,
          item_name:
            item.SalesItemLineDetail?.ItemRef?.name || matchedProd?.Name || "",
        };
      });

    const parsedNote = parsePrivateNote(detail?.raw?.PrivateNote);
    const lastLine = detail?.raw?.Line?.find(
      (l) => l.DetailType === "DiscountLineDetail",
    );
    const isDiscount = lastLine?.DiscountLineDetail;
    setFormData((prev) => ({
      ...prev,
      PrivateNote: parsedNote?.raw?.memo || "",
      AcceptedBy: detail?.raw?.CustomerRef?.name || prev.AcceptedBy,
      AcceptedDate:
        moment(detail.raw?.MetaData?.LastUpdatedTime).format("YYYY-MM-DD") ||
        prev.AcceptedDate,
      vendor_details: parsedNote.vendor_details || prev.vendor_details,
      store_name: parsedNote.store_name || prev.store_name,
      TrackingNum: parsedNote.tracking_num || prev.TrackingNum,
      ShipDate: parsedNote.ship_date || prev.ShipDate,
      CustomerRef: matchedCustomer?.Id || "",
      po_number:
        detail?.raw?.CustomField?.find((f) => f.DefinitionId === "3")
          ?.StringValue || prev.po_number,
      BillEmail:
        detail?.raw?.email ||
        matchedCustomer?.PrimaryEmailAddr?.Address ||
        prev.BillEmail,
      BillAddr: buildAddr(bill) || prev.BillAddr,
      ShipAddr: buildAddr(ship) || prev.ShipAddr,
      billingAddress: {
        bill_line1: bill.Line1 || "",
        bill_line2: bill.bill_line2 || "",
        bill_city: bill.City || bill.Line2 || "",
        bill_country: bill.Country || bill.Line3 || "",
        bill_postal_code: bill.PostalCode || "",
      },
      shippingAddress: {
        ship_line1: ship.Line1 || "",
        ship_line2: ship.ship_line2 || "",
        ship_city: ship.City || ship.Line2 || "",
        ship_country: ship.Country || ship.Line3 || "",
        ship_postal_code: ship.PostalCode || "",
      },
      DocNumber:
        detail?.raw?.CustomField?.find((f) => f.DefinitionId === "1")
          ?.StringValue ||
        detail.DocNumber ||
        prev.DocNumber,
      ExpirationDate: detail.raw?.ExpirationDate || prev.ExpirationDate,
      CustomerMemo: detail.raw?.CustomerMemo?.value || prev.CustomerMemo,
      EmailStatus: detail.raw?.EmailStatus || prev.EmailStatus,
      TxnDate: detail.raw?.txn_date || prev.TxnDate,
      tax_type: detail.raw?.tax_type || prev.tax_type,
      tax_rate_id: detail.raw?.tax_rate_id || prev.tax_rate_id,
      total_tax: detail.raw?.TxnTaxDetail?.TotalTax ?? prev.total_tax,
      markup_value: detail?.markup?.value ?? prev.markup_value,
      markup_type: detail?.markup?.type || prev.markup_type,
      discount_value: isDiscount
        ? lastLine.DiscountLineDetail?.PercentBased
          ? lastLine.DiscountLineDetail?.DiscountPercent || 0
          : lastLine.Amount || 0
        : 0,
      discount_amt: isDiscount ? lastLine.Amount || 0 : 0,
      discount_type: isDiscount
        ? lastLine.DiscountLineDetail?.PercentBased
          ? "percent"
          : "fixed"
        : "",
      Line: mappedLines.length ? mappedLines : prev.Line,
      TotalAmt: mappedLines.reduce((acc, l) => acc + l.Amount, 0),
      tax_percent: detail?.taxDetails?.tax_percent || 0,
      // preserve or load partial invoice fields across autofill
      invoiceType: detail.estimateType || detail.raw?.estimateType || detail.mainRequest?.estimateType || prev.invoiceType || "full",
      partialValue: (detail.estimateType || detail.raw?.estimateType || detail.mainRequest?.estimateType) === "partial"
        ? (detail.partialValue !== undefined && detail.partialValue !== null ? detail.partialValue : (detail.raw?.partialValue ?? detail.mainRequest?.partialValue ?? ""))
        : (prev.invoiceType === "partial" ? prev.partialValue : ""),
      partialMode: detail.partialMode || detail.raw?.partialMode || detail.mainRequest?.partialMode || prev.partialMode || "fixed",
    }));
  };

  // ─── Data fetching ────────────────────────────────────────────────────────
  const fetchInitialData = async () => {
    loader(true);
    try {
      const [custRes, prodRes] = await Promise.all([
        ApiClient.post("quickbook/customers", {
          quickbook_token: user?.quick_book_accesToken || "",
          company: user?.quick_book_companyId,
          email: formData.email,
        }),
        ApiClient.post("quickbook/items/db", {
          quickbook_token: user?.quick_book_accesToken || "",
          company: user?.quick_book_companyId,
        }),
      ]);

      const custList = custRes?.success ? custRes.data || [] : [];
      const prodList = prodRes?.success
        ? (prodRes.data || []).map((item) => ({
          ...item,
          Name: item?.name,
          Description: stripHtmlTags(item?.description),
          Id: item?.qbId,
          UnitPrice: item?.unitPrice,
        }))
        : [];

      customersRef.current = custList;
      productsRef.current = prodList;
      setCustomers(custList);
      setProducts(prodList);

      if (detailRef.current) tryAutofill(custList, prodList, detailRef.current);
      setTimeout(() => {
        loader(false);
      }, 300);
    } catch (err) {
      console.error(err);
      setTimeout(() => {
        loader(false);
      }, 300);
    }
  };

  const fetchRequestQuoteDetail = async () => {
    if (!id) return;
    loader(true);
    try {
      const res = await ApiClient.post("quickbook/single/estimate", {
        estimateId: id,
        token: user?.quick_book_accesToken || "",
        company: user?.quick_book_companyId,
      });
      if (res?.success && res?.data) {
        detailRef.current = res.data;
        setFormData((prev) => ({
          ...prev,
          email: res.data?.email || prev.BillEmail,
        }));
        if (customersRef.current.length > 0 || productsRef.current.length > 0) {
          tryAutofill(customersRef.current, productsRef.current, res.data);
        }
      }
    } catch (err) {
      console.error(err);
      swal({
        icon: "error",
        title: "Error",
        text: "Failed to load request quote details",
        confirmButtonColor: "#115B9D",
      });
    }
  };

  useEffect(() => {
    if (formData.email) fetchInitialData();
    fetchRequestQuoteDetail();
  }, [formData.email]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.Line];
    if (field === "ItemRef") {
      const product = products.find((p) => p.Id === value);
      if (!product) return;
      newLines[index].SalesItemLineDetail.ItemRef = {
        value: product.Id,
        name: product.Name,
      };
      newLines[index].item_name = product.Name;
      newLines[index].Description = stripHtmlTags(product.Description) || "";
      newLines[index].SalesItemLineDetail.UnitPrice = product.UnitPrice || 0;
    } else if (field === "Qty") {
      newLines[index].SalesItemLineDetail.Qty = Number(value);
    } else if (field === "UnitPrice") {
      newLines[index].SalesItemLineDetail.UnitPrice = Number(value);
    } else {
      newLines[index][field] = value;
    }
    newLines[index].Amount =
      newLines[index].SalesItemLineDetail.Qty *
      newLines[index].SalesItemLineDetail.UnitPrice;
    setFormData((prev) => ({
      ...prev,
      Line: newLines,
      TotalAmt: newLines.reduce((acc, l) => acc + l.Amount, 0),
    }));
  };

  const handleSubmit = async (e, date = "") => {
    e.preventDefault();

    // ─── Partial invoice validation ─────────────────────────────────────────
    if (!validatePartial()) return;

    const data = formData;
    try {
      const payload = {
        quickbook_token: user?.quick_book_accesToken ?? undefined,
        company: user?.quick_book_companyId ?? undefined,
        CustomerRef: { value: data.CustomerRef ?? undefined },
        TxnDate: data.TxnDate ?? undefined,
        PrivateNote: data.PrivateNote ?? undefined,
        EmailStatus: data.EmailStatus ?? undefined,
        CustomerMemo: { value: data.CustomerMemo ?? undefined },
        ShipDate: date || "",
        markup_type: data.markup_type,
        markup_value: Number(data.markup_value),
        TxnTaxDetail: { TxnTaxCodeRef: { value: "6" } },
        BillEmail: { Address: data.BillEmail } ?? {
          Address: "test@yopmail.com",
        },
        BillAddr: data.BillAddr
          ? {
            Line1: data.billingAddress.bill_line1 ?? undefined,
            City: data.billingAddress.bill_city ?? undefined,
            PostalCode: data.billingAddress.bill_postal_code ?? undefined,
            Country: data.billingAddress.bill_country ?? undefined,
          }
          : null,
        ShipAddr: data.ShipAddr
          ? {
            Line1: data.shippingAddress.ship_line1 ?? undefined,
            City: data.shippingAddress.ship_city ?? undefined,
            Country: data.shippingAddress.ship_country ?? undefined,
            PostalCode: data.shippingAddress.ship_postal_code ?? undefined,
          }
          : undefined,
        Line: Array.isArray(data.Line)
          ? data.Line.map((line) => {
            if (line.DetailType === "SalesItemLineDetail") {
              return {
                Amount: line.Amount ?? undefined,
                DetailType: line.DetailType ?? undefined,
                Description: stripHtmlTags(line.Description) ?? undefined,
                SalesItemLineDetail: line.SalesItemLineDetail
                  ? {
                    ItemRef: line.SalesItemLineDetail.ItemRef ?? undefined,
                    Qty: line.SalesItemLineDetail.Qty ?? undefined,
                    UnitPrice:
                      line.SalesItemLineDetail.UnitPrice ?? undefined,
                    TaxCodeRef: { value: "TAX" },
                  }
                  : undefined,
                LinkedTxn: [{ TxnId: id, TxnType: "Estimate" }],
              };
            }
            if (line.DetailType === "DiscountLineDetail") {
              return {
                Amount: line.Amount ?? undefined,
                DetailType: line.DetailType ?? undefined,
                DiscountLineDetail: line.DiscountLineDetail ?? undefined,
              };
            }
            return {
              Amount: line.Amount ?? undefined,
              DetailType: line.DetailType ?? undefined,
            };
          })
          : undefined,
        // ─── Partial invoice payload keys ───────────────────────────────────
        invoiceType: data.invoiceType,
        partialValue:
          data.invoiceType === "partial"
            ? Number(data.partialValue)
            : undefined,
        partialMode:
          data.invoiceType === "partial" ? data.partialMode : undefined,
        partialAmount:
          data.invoiceType === "partial" && computedPartialAmt !== null
            ? Number(methodModel.truncateDecimal(computedPartialAmt || 0))
            : undefined,
      };

      loader(true);
      const res = await ApiClient.post("quickbook/create/invoice", payload);
      if (res?.success) {
        setShowShipDateModal(false);
        loader(false);
        const action = await swal({
          icon: "success",
          title: "Invoice Created Successfully!",
          text: `Your invoice has been created successfully.\nClick the button below to view the invoice details.`,
          buttons: {
            list: {
              text: "Go Back",
              value: "list",
            },
            view: {
              text: "View Invoice",
              value: "view",
            },
          },
        });

        if (action === "view") {
          navigate(
            `/invoice/detail/${res?.data?.invoice?._id || res?.data?.invoice?.id}`,
          );
        }

        if (action === "list") {
          navigate(`/order/management?tab=invoice`);
        }
      }
    } catch (err) {
      swal({
        icon: "error",
        title: "Error",
        text: "An error occurred while creating the estimate",
        confirmButtonColor: "#115B9D",
      });
    }
    loader(false);
  };

  const submitInvoice = (e, date, type) => {
    if (type && !date) {
      toast.error("Please enter a date, otherwise click the Skip button.");
      return;
    }
    handleSubmit(e, date);
  };

  // ─── Open ship-date modal — validate partial first ────────────────────────
  const handleGenerateInvoiceClick = () => {
    if (!validatePartial()) return;
    setShowShipDateModal(true);
  };

  // ─── Shared input class ───────────────────────────────────────────────────
  const inputCls =
    "h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300";
  const disabledCls = `${inputCls} bg-gray-50`;

  return (
    <>
      <Layout>
        <div className="max-xl:container xl:max-w-[1280px]  mx-auto max-w-6xl py-5">
          <div className="bg-white shadow-lg rounded-2xl border border-gray-100 px-5 sm:px-6 py-6">
            {/* Header */}
            {/* Header */}
            <div className="flex items-center justify-between mb-8 border-b pb-4">
              <div className="flex items-center gap-3">
                {/* Back Button */}
                <button
                  type="button"
                  onClick={() => navigate(`/order/management?tab=estimate`)}
                  className="w-11 h-11 rounded-full border border-gray-300 bg-white hover:bg-gray-50 hover:border-[#115B9D] transition-all duration-200 flex items-center justify-center shadow-sm"
                >
                  <IoArrowBack
                    size={20}
                    className="text-[#115B9D]"
                  />
                </button>

                <h1 className="text-xl sm:text-2xl font-bold text-[#115B9D]">
                  Convert To Invoice
                </h1>
              </div>
            </div>

            <form className="space-y-8">
              {/* Customer row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Customer
                  </label>
                  <div className="capitalize ">
                    <FormControl
                      disabled
                      className="capitalize"
                      placeholder="Select Customer"
                      theme="search"
                      type="select"
                      displayValue="DisplayName"
                      options={customers?.map((c) => ({
                        id: c.Id,
                        DisplayName: c.DisplayName,
                      }))}
                      value={formData.CustomerRef}
                      onChange={(e) =>
                        setFormData({ ...formData, CustomerRef: e })
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Customer Email
                  </label>
                  <input
                    disabled
                    type="email"
                    name="BillEmail"
                    className={disabledCls}
                    value={formData.BillEmail}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Address / meta grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {/* Bill to */}
                <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Bill to
                    </label>
                    <textarea
                      disabled
                      name="BillAddr"
                      rows="4"
                      className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200"
                      value={formData.BillAddr}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Ship via
                    </label>
                    <input
                      disabled
                      type="text"
                      name="ShipVia"
                      className={disabledCls}
                      value="Ship via"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Tracking no.
                    </label>
                    <input
                      disabled
                      type="text"
                      name="TrackingNum"
                      className={disabledCls}
                      value={formData.TrackingNum}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Ship to */}
                <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Ship to
                    </label>
                    <textarea
                      disabled
                      name="ShipAddr"
                      rows="4"
                      className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200"
                      value={formData.ShipAddr}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                        Estimate no.
                      </label>
                      <input
                        disabled
                        type="text"
                        name="DocNumber"
                        className={disabledCls}
                        value={formData.DocNumber}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                        Estimate date
                      </label>
                      <input
                        disabled
                        type="date"
                        name="TxnDate"
                        className={disabledCls}
                        value={formData.TxnDate}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Expiration date
                    </label>
                    <input
                      disabled
                      type="date"
                      name="ExpirationDate"
                      className={disabledCls}
                      value={formData.ExpirationDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Ship from + accepted */}
                <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Ship from (hidden)
                    </label>
                    <textarea
                      disabled
                      rows="4"
                      readOnly
                      className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200"
                      value={"2000 Avenue P Ste 9\nRiviera Beach, FL 33404"}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                        Accepted by
                      </label>
                      <input
                        disabled
                        type="text"
                        name="AcceptedBy"
                        className={disabledCls}
                        value={formData.AcceptedBy}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                        Accepted date
                      </label>
                      <input
                        disabled
                        type="date"
                        name="AcceptedDate"
                        className={disabledCls}
                        value={formData.AcceptedDate}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Store / PO / Vendor */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 pt-4 border-t border-gray-50">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Store Name
                  </label>
                  <input
                    disabled
                    type="text"
                    name="store_name"
                    className={disabledCls}
                    value={formData.store_name}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    PO Number
                  </label>
                  <input
                    disabled
                    type="text"
                    name="po_number"
                    className={disabledCls}
                    value={formData.po_number}
                    onChange={handleInputChange}
                  />
                  <span className="text-[10px] text-gray-600 mt-1">
                    (hidden)
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Vendor Details
                  </label>
                  <input
                    disabled
                    type="text"
                    name="vendor_details"
                    className={disabledCls}
                    value={formData.vendor_details}
                    onChange={handleInputChange}
                  />
                  <span className="text-[10px] text-gray-600 mt-1">
                    (hidden)
                  </span>
                </div>
              </div>

              {/* Line items */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Product or service
                </h2>

                {/* Desktop Table - unchanged, hidden on mobile */}
                <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-2xl">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-600 w-12">
                          #
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-600">
                          Product/Service
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-600">
                          Description
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600 w-24">
                          Qty
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600 w-32">
                          Rate
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600 w-32">
                          Amount
                        </th>
                        <th className="px-4 py-3 w-12"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {formData.Line.map((line, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                          <td className="px-4 py-3">
                            <div className="capitalize">
                              <FormControl
                                disabled
                                className="capitalize"
                                placeholder="Select QuickBook Product"
                                theme="search"
                                type="select"
                                displayValue="DisplayName"
                                options={products?.map((c) => ({
                                  id: c.Id || c._id,
                                  DisplayName: c.Name,
                                }))}
                                value={line.SalesItemLineDetail.ItemRef.value || ""}
                                onChange={(e) => handleLineChange(index, "ItemRef", e)}
                              />
                            </div>
                            {line.item_name && !line.SalesItemLineDetail.ItemRef.value && (
                              <p className="text-[11px] text-amber-500 mt-1">
                                Requested: {line.item_name}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <input
                              disabled
                              type="text"
                              className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200 min-w-[150px]"
                              value={line.Description}
                              onChange={(e) =>
                                handleLineChange(index, "Description", e.target.value)
                              }
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              disabled
                              type="number"
                              step="any"
                              min="1"
                              className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200 min-w-[65px] text-right"
                              value={line.SalesItemLineDetail.Qty}
                              onChange={(e) => handleLineChange(index, "Qty", e.target.value)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              disabled
                              type="number"
                              step="any"
                              min="0"
                              className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200 min-w-[100px] text-right"
                              value={line.SalesItemLineDetail.UnitPrice || ""}
                              onChange={(e) =>
                                handleLineChange(index, "UnitPrice", e.target.value)
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            ${methodModel.truncateDecimal(line.Amount || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards - shown only on mobile */}
                <div className="md:hidden space-y-3">
                  {formData.Line.map((line, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-2xl p-4 bg-white"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-gray-500">
                          Item #{index + 1}
                        </span>
                        <span className="text-sm font-semibold text-gray-800">
                          ${methodModel.truncateDecimal(line.Amount || 0)}
                        </span>
                      </div>

                      <div className="mb-3">
                        <label className="text-xs font-medium text-gray-500 mb-1 block">
                          Product/Service
                        </label>
                        <div className="capitalize">
                          <FormControl
                            disabled
                            className="capitalize"
                            placeholder="Select QuickBook Product"
                            theme="search"
                            type="select"
                            displayValue="DisplayName"
                            options={products?.map((c) => ({
                              id: c.Id || c._id,
                              DisplayName: c.Name,
                            }))}
                            value={line.SalesItemLineDetail.ItemRef.value || ""}
                            onChange={(e) => handleLineChange(index, "ItemRef", e)}
                          />
                        </div>
                        {line.item_name && !line.SalesItemLineDetail.ItemRef.value && (
                          <p className="text-[11px] text-amber-500 mt-1">
                            Requested: {line.item_name}
                          </p>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="text-xs font-medium text-gray-500 mb-1 block">
                          Description
                        </label>
                        <input
                          disabled
                          type="text"
                          className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200"
                          value={line.Description}
                          onChange={(e) =>
                            handleLineChange(index, "Description", e.target.value)
                          }
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">
                            Qty
                          </label>
                          <input
                            disabled
                            type="number"
                            step="any"
                            min="1"
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200 text-right"
                            value={line.SalesItemLineDetail.Qty}
                            onChange={(e) => handleLineChange(index, "Qty", e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">
                            Rate
                          </label>
                          <input
                            disabled
                            type="number"
                            step="any"
                            min="0"
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200 text-right"
                            value={line.SalesItemLineDetail.UnitPrice || ""}
                            onChange={(e) =>
                              handleLineChange(index, "UnitPrice", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-8 lg:gap-12">
                {/* Memos */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                      Customer payment options
                    </h3>
                    <textarea
                      disabled
                      name="CustomerMemo"
                      rows="2"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200"
                      value={formData.CustomerMemo}
                      onChange={handleInputChange}
                      placeholder="Tell your customer how you want to get paid."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Memo on statement (hidden)
                    </label>
                    <textarea
                      disabled
                      name="PrivateNote"
                      rows="2"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none border-gray-200"
                      value={formData.PrivateNote}
                      onChange={handleInputChange}
                      placeholder="This memo will not show up on your estimate, but will appear on the statement."
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="space-y-4">
                  <div className="bg-gray-50/50 rounded-2xl space-y-4 p-5">
                    {/* Subtotal */}
                    <div className="flex gap-2 justify-between items-center text-sm">
                      <span className="text-gray-500 font-medium">
                        Base Cost
                      </span>
                      <span className="text-gray-900 font-semibold">
                        $
                        {methodModel.truncateDecimal(subtotalbeforemarkup || 0)}
                      </span>
                    </div>
                    {/* Markup — editable */}
                    <div className="flex gap-2 justify-between items-center text-sm">
                      <span className="text-gray-500 font-medium shrink-0">
                        Markup
                      </span>
                      <div className="flex flex-wrap justify-end items-center gap-2 ml-auto">
                        <input
                          type="number"
                          step="any"
                          min="0"
                          onWheel={(e) => e.target.blur()}
                          name="markup_value"
                          disabled
                          className="w-24 p-1.5 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                          value={formData.markup_value}
                          onChange={handleInputChange}
                        />
                        <div className="select_control">
                          <FormControl
                            disabled
                            placeholder="Select Markup Type"
                            theme="search"
                            type="select"
                            options={[
                              { name: "$", id: "fixed" },
                              { name: "%", id: "percentage" },
                            ]}
                            value={formData.markup_type}
                            onChange={(e) =>
                              setFormData({ ...formData, markup_type: e })
                            }
                          />
                        </div>
                        <span className="text-gray-900 font-semibold w-20 text-right">
                          +${methodModel.truncateDecimal(markupAmt || 0)}
                        </span>
                      </div>
                    </div>

                    {/* Subtotal */}
                    <div className="flex gap-2 justify-between items-center text-sm border-t border-gray-200 pt-2">
                      <span className="text-gray-500 font-medium">
                        Subtotal
                      </span>
                      <span className="text-gray-900 font-semibold">
                        ${methodModel.truncateDecimal(subtotal || 0)}
                      </span>
                    </div>

                    {/* Tax */}
                    <div className="flex gap-2 justify-between items-center text-sm">
                      <span className="text-gray-500 font-medium shrink-0">
                        Sales tax{" "}
                        <svg
                          className="inline size-3.5 ml-1 text-gray-600"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                      </span>
                      <div className="flex items-center gap-2 ml-auto">
                        <div className="flex items-center border rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-[#115B9D]/30 focus-within:border-[#115B9D] border-gray-200 transition-all">
                          <input
                            disabled
                            type="number"
                            step="any"
                            min="0"
                            max="100"

                            name="tax_percent"
                            className="w-16 p-1.5 text-right text-sm text-gray-800 outline-none bg-white"
                            value={formData.tax_percent}
                            onChange={handleInputChange}
                          />
                          <span className="px-2 text-gray-500 text-sm bg-gray-50 border-l border-gray-200 select-none">
                            %
                          </span>
                        </div>
                        <span className="text-gray-900 font-semibold w-20 text-right">
                          ${methodModel.truncateDecimal(taxAmt)}
                        </span>
                      </div>
                    </div>

                    {/* Discount */}
                    <div className="flex gap-2 justify-between items-center text-sm">
                      <span className="text-gray-500 font-medium shrink-0">
                        Discount
                      </span>
                      <div className="flex flex-wrap justify-end items-center gap-2 ml-auto">
                        <input
                          disabled
                          type="number"
                          step="any"
                          min="0"
                          name="discount_value"
                          className="w-24 p-1.5 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                          value={formData.discount_value}
                          onChange={handleInputChange}
                        />
                        <div className="select_control">
                          <FormControl
                            disabled
                            placeholder="Select Discount Type"
                            theme="search"
                            type="select"
                            options={[
                              { name: "$", id: "fixed" },
                              { name: "%", id: "percent" },
                            ]}
                            value={formData.discount_type}
                            onChange={(e) =>
                              setFormData({ ...formData, discount_type: e })
                            }
                          />
                        </div>
                        <span className="text-gray-900 font-semibold w-20 text-right">
                          -${methodModel.truncateDecimal(discountAmt)}
                        </span>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="flex gap-2 justify-between items-center pt-2 border-t border-gray-200">
                      <span className="text-base font-bold text-gray-900">
                        Estimate total
                      </span>
                      <span className="text-2xl font-bold text-[#115B9D]">
                        ${roundSmart(estimateTotal)}
                      </span>
                    </div>

                    {/* ─── Invoice Type (partial / full) ──────────────────── */}
                    <div className="border-t border-gray-200 pt-4 space-y-3">
                      <div className="flex gap-2 justify-between items-center text-sm">
                        <span className="text-gray-500 font-medium shrink-0">
                          Invoice Type
                        </span>
                        <div className="select_control capitalize">
                          <FormControl
                            placeholder="Select Discount Type"
                            theme="search"
                            type="select"
                            options={[
                              { name: "Full", id: "full" },
                              { name: "Partial", id: "partial" },
                            ]}
                            value={formData.invoiceType}
                            onChange={(e) => {
                              const next = e;
                              setFormData((prev) => ({
                                ...prev,
                                invoiceType: next,
                                partialValue:
                                  next === "full" ? "" : prev.partialValue,
                              }));
                              if (next === "full") setPartialError("");
                            }}
                          />
                        </div>
                      </div>

                      {/* Partial sub-row — only when "partial" is selected */}
                      {formData.invoiceType === "partial" && (
                        <div className="space-y-2">
                          <div className="flex flex-wrap justify-between items-center gap-2 text-sm">
                            <span className="text-gray-500 font-medium shrink-0">
                              Partial Amount
                            </span>
                            <div className="flex items-center gap-2 ml-auto">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                onWheel={(e) => e.target.blur()}
                                name="partialValue"
                                placeholder={
                                  formData.partialMode === "percentage"
                                    ? "e.g. 20"
                                    : "e.g. 100"
                                }
                                className={`w-28 p-1.5 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${partialError
                                  ? "border-red-400 focus:ring-red-300"
                                  : "border-gray-200"
                                  }`}
                                value={formData.partialValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    partialValue: val,
                                  }));
                                  // clear error on change
                                  if (partialError) setPartialError("");
                                }}
                              />
                              <div className="select_control capitalize">
                                <FormControl
                                  placeholder="Select Discount Type"
                                  theme="search"
                                  type="select"
                                  options={[
                                    { name: "$", id: "fixed" },
                                    { name: "%", id: "percentage" },
                                  ]}
                                  value={formData.partialMode}
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      partialMode: e,
                                      partialValue: "",
                                    }));
                                    setPartialError("");
                                  }}
                                />
                              </div>

                              {/* Live computed partial amount preview */}
                              {computedPartialAmt !== null && (
                                <span className="text-gray-900 font-semibold w-20 text-right">
                                  $
                                  {methodModel.truncateDecimal(
                                    computedPartialAmt || 0,
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Validation error message */}
                          {partialError && (
                            <p className="text-xs text-red-500 text-right">
                              {partialError}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    {/* ─────────────────────────────────────────────────────── */}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap items-center justify-end border-t mt-12 pt-6">
                <button
                  type="button"
                  onClick={() => navigate(`/order/management?tab=estimate`)}
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGenerateInvoiceClick}
                  className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
                >
                  Generate Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      </Layout>

      {/* Ship date modal */}
      {showShipDateModal && (
        <div className="fixed inset-0 z-[99] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-lg font-bold text-gray-800 mb-1">
              Estimated Shipping Date
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Enter an estimated shipping date or skip to proceed without one.
            </p>
            <input
              type="date"
              className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 mb-4"
              value={formData.ShipDate}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, ShipDate: e.target.value }))
              }
            />
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={(e) => submitInvoice(e, "", "")}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={(e) => submitInvoice(e, formData.ShipDate, "required")}
                className="inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
